import { memo, useCallback, useState } from 'react'

import { AutoDeleteOutlined, ControlPointDuplicate } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem, Skeleton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { LifecyclePolicyDialog } from 'domains/Workspaces/Audio/Streams'
import { useAudioStreamCreateMutation, useAudioStreamDeleteMutation } from 'hooks/mutations/content'
import { audioStreamsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Streams'
import { audioStreamRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Streams/Stream'
import { audioStreamQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { audiostreamId } = audioStreamRoute.useParams()
  const navigate = useNavigate({ from: audioStreamRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
  const [dialog, setDialog] = useState(false)

  const audioStreamQuery = useQuery(audioStreamQueryKeys.detail({ streamId: audiostreamId }))
  const { data: audioStream } = audioStreamQuery

  const { mutate: createAudioStream, ...audioStreamCreateMutation } = useAudioStreamCreateMutation()
  const { mutate: deleteAudioStream } = useAudioStreamDeleteMutation()

  const handleDeleteAudioStream = useCallback(() => {
    if (!audioStream) return

    setAnchorEl(undefined)

    deleteAudioStream(
      { streamId: audioStream.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Workspace.Audio.nameDelete', { name: audioStream.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: audioStreamsOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [audioStream, deleteAudioStream, enqueueSnackbar, navigate, t])

  const handleDuplicateAudioStream = useCallback(() => {
    if (!audioStream) return

    setAnchorEl(undefined)

    createAudioStream(
      { audioStreamInsert: { ...audioStream, name: `${audioStream.name} (${t('duplicate')})` } },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [createAudioStream, audioStream, enqueueSnackbar, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={audioStream?.name}
        entity={t('entity:content.audioStream', { count: 1 })}
      >
        <MenuItem onClick={handleDuplicateAudioStream}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {audioStreamCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setAnchorEl(undefined)
            setDialog(true)
          }}
        >
          <ListItemIcon>
            <AutoDeleteOutlined />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {t('domain:Workspace.Audio.setContentLifecyclePolicy', { count: 1 })}
          </Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={audiostreamId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:Workspace.Audio.deleteStream', { count: 1 })}
          onConfirm={handleDeleteAudioStream}
        />
      </ActionMenu>
      <Dialog fullWidth open={dialog} onClose={() => setDialog(false)}>
        {audioStream ? <LifecyclePolicyDialog streamData={audioStream} onSuccess={() => setDialog(false)} /> : null}
      </Dialog>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
